import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, Box, Typography, Button, IconButton, alpha, CircularProgress } from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import { CaixaTurnoService, ICaixaPdvItem } from '../../shared/services/api/Venda/CaixaTurnoService';
import { usuarioLogado } from '../../shared/offline/fila';
import { PDVThemeColors } from './PDVThemes';

// Caixa com sessão aberta por OUTRO usuário — só quem abriu a sessão entra
// nesse caixa (o backend também recusa venda nele, ver Createpdv). Usado
// aqui (bloqueia o clique) e em Pdv.tsx (não entra direto num caixa lembrado
// que agora está com outro operador).
export const caixaEmUsoPorOutro = (caixa: ICaixaPdvItem, iduser = usuarioLogado().iduser) =>
  caixa.aberto && !!caixa.iduserSessao && caixa.iduserSessao !== iduser;

interface DialogSelecionarCaixaProps {
  colors: PDVThemeColors;
  onSelecionar: (idconta: string) => void;
  // Sempre aberta e tela cheia (substitui a tela toda do PDV) quando ainda
  // não existe idContaSelecionada nenhum — nesse ponto não há nada do PDV
  // pra mostrar por trás mesmo. Quando reaberta depois (ex.: F2 com o caixa
  // fechado, pra trocar de caixa), vem como uma dialog normal por cima da
  // tela do PDV (que já tem conteúdo pra mostrar atrás), não mais em tela
  // cheia — ver open/onClose/fullScreen abaixo.
  open?: boolean;
  onClose?: () => void;
  fullScreen?: boolean;
}

const Shell: React.FC<{ colors: PDVThemeColors; open: boolean; onClose?: () => void; fullScreen: boolean; children: React.ReactNode }> = ({ colors, open, onClose, fullScreen, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullScreen={fullScreen}
    maxWidth={fullScreen ? undefined : 'xs'}
    fullWidth={!fullScreen}
    disableEscapeKeyDown={!onClose}
    PaperProps={fullScreen ? {
      sx: {
        background: colors.backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      },
    } : { sx: { borderRadius: '16px' } }}
  >
    <Box sx={{ height: fullScreen ? '100%' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', p: fullScreen ? 2 : 0, position: 'relative' }}>
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: fullScreen ? colors.textOnPrimary : 'text.secondary',
            backgroundColor: fullScreen ? alpha(colors.textOnPrimary, 0.12) : 'transparent',
            '&:hover': { backgroundColor: fullScreen ? alpha(colors.textOnPrimary, 0.22) : alpha('#000', 0.06) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        sx={{
          width: '100%',
          maxWidth: fullScreen ? 420 : undefined,
          borderRadius: '20px',
          p: 4,
          textAlign: 'center',
          backgroundColor: fullScreen ? alpha('#FFFFFF', 0.96) : 'transparent',
          boxShadow: fullScreen ? '0 20px 60px rgba(0,0,0,0.35)' : 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  </Dialog>
);

const IconBadge: React.FC<{ colors: PDVThemeColors; icon: React.ReactNode }> = ({ colors, icon }) => (
  <Box
    sx={{
      width: 64,
      height: 64,
      borderRadius: '18px',
      mx: 'auto',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.primaryGradient,
    }}
  >
    {icon}
  </Box>
);

// Tela exibida antes de tudo no PDV: o operador escolhe qual caixa
// pré-cadastrado vai trabalhar nessa sessão (ver CaixaTurnoService.listarCaixas
// / backend GET /caixaturno/caixas). Um caixa já aberto pode ser escolhido
// normalmente — pode ser o mesmo operador retomando após um refresh, ou
// outra pessoa continuando o mesmo turno; quem trava abrir um SEGUNDO turno
// no mesmo caixa é o backend (abrirTurnoPorConta), não esta tela.
export const DialogSelecionarCaixa: React.FC<DialogSelecionarCaixaProps> = ({
  colors, onSelecionar, open = true, onClose, fullScreen = true,
}) => {
  const [caixas, setCaixas] = useState<ICaixaPdvItem[] | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro('');
    CaixaTurnoService.listarCaixas().then((resultado) => {
      setCarregando(false);
      if (resultado instanceof Error) {
        setErro(resultado.message);
        return;
      }
      setCaixas(resultado);
    });
  }, []);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  return (
    <Shell colors={colors} open={open} onClose={onClose} fullScreen={fullScreen}>
      <IconBadge colors={colors} icon={<PointOfSaleIcon sx={{ fontSize: 32, color: colors.textOnPrimary }} />} />

      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Selecione o caixa
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Escolha em qual caixa você vai trabalhar.
      </Typography>

      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!carregando && erro && (
        <>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {erro}
          </Typography>
          <Button variant="contained" onClick={carregar}>
            Tentar novamente
          </Button>
        </>
      )}

      {!carregando && !erro && caixas?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nenhum caixa disponível. Cadastre um caixa PDV na tela de contas bancárias.
        </Typography>
      )}

      {!carregando && !erro && caixas && caixas.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {caixas.map((caixa) => {
            // Fechado por Conciliação hoje bloqueia mesmo sem sessão ativa —
            // sem essa checagem o operador clicava, preenchia o valor de
            // abertura em DialogAbrirCaixa e só ali tomava o erro do backend
            // ("Conta de Credito Fechada nesta Data").
            const fechadoPorConciliacao = !caixa.aberto && !!caixa.fechadoHoje;
            const emUsoPorOutro = caixaEmUsoPorOutro(caixa);
            const minhaSessao = caixa.aberto && !emUsoPorOutro;
            const bloqueado = fechadoPorConciliacao || emUsoPorOutro;

            return (
              <Box
                key={caixa.idconta}
                onClick={bloqueado ? undefined : () => onSelecionar(caixa.idconta)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1.5,
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  cursor: bloqueado ? 'not-allowed' : 'pointer',
                  opacity: bloqueado ? 0.6 : 1,
                  textAlign: 'left',
                  '&:hover': bloqueado ? undefined : { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {caixa.titular}
                  </Typography>
                  {emUsoPorOutro && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      Em uso por {caixa.nomeOperador || 'outro operador'} — só quem abriu a sessão pode acessar
                    </Typography>
                  )}
                  {minhaSessao && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Sua sessão está aberta neste caixa
                    </Typography>
                  )}
                  {caixa.aberto && caixa.valorabertura !== undefined && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Aberto em {caixa.valorabertura.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Typography>
                  )}
                  {fechadoPorConciliacao && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      Fechado por conciliação — reabra a conta para usar hoje
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    borderRadius: '8px',
                    // `aberto` indica se JÁ existe sessão/turno ativo nesse caixa
                    // (em uso), não se ele está disponível — por isso o badge é
                    // invertido: sem sessão ativa = disponível pra abrir. Um
                    // terceiro estado (bloqueado) cobre o caixa fechado pela
                    // Conciliação, que também não pode ser aberto hoje.
                    backgroundColor: bloqueado
                      ? alpha('#c62828', 0.12)
                      : minhaSessao ? alpha('#1565c0', 0.12) : alpha('#2e7d32', 0.12),
                    color: bloqueado ? '#c62828' : minhaSessao ? '#1565c0' : '#2e7d32',
                  }}
                >
                  {bloqueado ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                  <Typography variant="caption" fontWeight={700}>
                    {fechadoPorConciliacao ? 'Fechado' : emUsoPorOutro ? 'Em uso' : minhaSessao ? 'Sua sessão' : 'Disponível'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Shell>
  );
};
